import { useState, useRef } from 'react';
import styled from 'styled-components';
import { subYears } from 'date-fns';
import { useSpring, animated } from 'react-spring';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { Grid } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { smWidth, WorksheetTextField, Heading } from '../../utils/CommonStyles';
import { ICompletionStatus, IWorksheetPurchaserInfo } from '../../types/worksheet';
import { validateEmail, validatePhone, isPersonalInfoFilled, sanitizePhone } from '../../utils/Validations';
import { formatPhoneNumber, scrollToRef } from '../../utils/Functions';

const PersonalInfo = (props: ChildProps) => {
  const {
    alignment,
    completionStatus,
    fields,
    handleTextInput,
    handleDateChange,
    handleNext,
    disabled,
    handleText,
    style,
    asterisk,
    submitted,
    checkWishlist,
  } = props;
  const { firstName, lastName, email, primaryPhone, dob, occupation, employer, corp } = fields;
  const isXS = useMediaQuery('(max-width: 480px)');
  const [dateError, setDateError] = useState<string | null>(null);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const animationProps = useSpring({
    to: { opacity: 1, y: 0 },
    from: { opacity: 0, y: 100 },
    delay: 500,
    padding: '10px',
  });

  const showErrors = !!submitted || attemptedNext;
  const firstNameMissing = showErrors && !firstName;
  const lastNameMissing = showErrors && !lastName;
  const occupationMissing = showErrors && !occupation;
  const employerMissing = showErrors && !employer;
  const emailHasError = showErrors && !disabled && !validateEmail(email) && email !== '';
  const phoneHasError = showErrors && !disabled && !validatePhone(primaryPhone) && primaryPhone !== '';
  const emailMissing = showErrors && !email;
  const phoneMissing = showErrors && !primaryPhone;

  const handleNextClick = (event: any) => {
    if (checkWishlist && !checkWishlist()) {
      return;
    }
    if (isPersonalInfoFilled(fields)) {
      handleNext(event);
    } else {
      setAttemptedNext(true);
      scrollToRef(containerRef.current);
    }
  };

  return (
    <div>
      <PersonalInfoContainer container spacing={2}>
        <Heading item xs={12}>
          <animated.div style={animationProps}>Information</animated.div>
        </Heading>

        <Grid item xs={12} ref={containerRef}>
          <animated.div style={animationProps}>
            <Grid container spacing={2}>
              <Grid item lg={4} md={4} sm={4} xs={12}>
                <WorksheetTextField
                  id="firstname-textarea"
                  label={alignment === 'personal' ? 'First Name' : 'Corporation Number'}
                  name="firstName"
                  sx={asterisk}
                  error={firstNameMissing}
                  helperText={firstNameMissing ? `Missing ${alignment === 'personal' ? 'First Name' : 'Corporation Number'}` : ''}
                  value={firstName}
                  onChange={handleTextInput}
                  disabled={disabled}
                  required
                />
              </Grid>
              <Grid item lg={4} md={4} sm={4} xs={12}>
                <WorksheetTextField
                  id="lastname-textarea"
                  label={alignment === 'personal' ? 'Last Name' : 'Corporation Name'}
                  name="lastName"
                  sx={asterisk}
                  error={lastNameMissing}
                  helperText={lastNameMissing ? `Missing ${alignment === 'personal' ? 'Last Name' : 'Corporation Name'}` : ''}
                  value={lastName}
                  onChange={handleTextInput}
                  disabled={disabled}
                  required
                />
              </Grid>
              <Grid item lg={4} md={4} sm={4} xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    disableFuture
                    value={dob}
                    label="Date of Birth"         
                    onChange={(newValue: any) => {
                      handleDateChange(newValue, 'dob');
                    }}
                    onError={(reason: any, value: any) => {
                      switch (reason) {
                        case 'invalidDate':
                          setDateError('Invalid Date');
                          break;
                        case 'disablePast':
                          setDateError('Invalid Date');
                          break;
                        case 'disableFuture':
                          setDateError('Purchaser cannot born in the future!');
                          break;
                        case 'maxDate':
                          setDateError('The purchaser is too young to purchase!');
                          break;
                        case 'minDate':
                          setDateError('Invalid Date');
                          break;
                        case 'shouldDisableDate':
                          // shouldDisableDate returned true, render custom message according to the `shouldDisableDate` logic
                          setDateError('Invalid Date');
                          break;
                        default:
                          if (value) {
                            setDateError(null);
                          } else {
                            setDateError('Date cannot be empty');
                          }
                      }
                    }}
                    sx={{ width: '100%'}}
                    maxDate={!corp ? subYears(new Date(), 18) : new Date()}
                    disabled={disabled}
                    slotProps={{ textField: { required: true } }}
                  />
                  {dateError ? <DateError>{dateError}</DateError> : null}
                </LocalizationProvider>
              </Grid>
              <Grid item lg={4} md={4} sm={4} xs={12}>
                <WorksheetTextField
                  id="email-textarea"
                  label="Email"
                  name="email"
                  type="email"
                  sx={emailHasError || emailMissing ? style : asterisk}
                  error={emailHasError || emailMissing}
                  helperText={emailHasError ? 'Please enter a valid email' : emailMissing ? 'Missing Email' : ''}
                  value={email}
                  onChange={handleTextInput}
                  disabled={disabled}
                  required
                />
              </Grid>
              <Grid item lg={4} md={4} sm={4} xs={12}>
                <WorksheetTextField
                  id="phone-textarea"
                  label="Phone Number"
                  name="primaryPhone"
                  sx={phoneHasError || phoneMissing ? style : asterisk}
                  error={phoneHasError || phoneMissing}
                  helperText={phoneHasError ? 'Please enter a valid phone number' : phoneMissing ? 'Missing Phone Number' : ''}
                  value={formatPhoneNumber(primaryPhone)}
                  onChange={(e) => {
                    handleText(e.target.name, sanitizePhone(e.target.value));
                  }}
                  disabled={disabled}
                  required
                />
              </Grid>
              <Grid item lg={4} md={4} sm={4} xs={12}>
                <WorksheetTextField
                  id="occupation-textarea"
                  label="Occupation"
                  name="occupation"
                  value={occupation}
                  sx={asterisk}
                  error={occupationMissing}
                  helperText={occupationMissing ? 'Missing Occupation' : ''}
                  onChange={handleTextInput}
                  disabled={disabled}
                  required
                />
              </Grid>
              <Grid item lg={4} md={4} sm={4} xs={12}>
                <WorksheetTextField
                  id="employer-textarea"
                  label="Employer"
                  name="employer"
                  value={employer}
                  sx={asterisk}
                  error={employerMissing}
                  helperText={employerMissing ? 'Missing Employer' : ''}
                  onChange={handleTextInput}
                  disabled={disabled}
                  required
                />
              </Grid>
            </Grid>
          </animated.div>
          {completionStatus.personalInfo ? (
            !isXS && (
              <animated.div style={animationProps}>
                <Divider sx={{ marginTop: '50px' }} />
              </animated.div>
            )
          ) : (
            <animated.div style={animationProps}>
              <Button
                disableElevation
                variant="contained"
                name="personalInfo"
                onClick={handleNextClick}
                sx={{
                  marginTop: '10px',
                  '@media (max-width: 480px)': {
                    marginLeft: '0',
                  },
                }}
              >
                Next
              </Button>
            </animated.div>
          )}
        </Grid>
      </PersonalInfoContainer>
    </div>
  );
};

interface ChildProps {
  alignment: string;
  completionStatus: ICompletionStatus;
  fields: IWorksheetPurchaserInfo;
  handleTextInput: any;
  handleDateChange: any;
  handleNext: any;
  handleText: (name: string, value: string) => void;
  disabled?: boolean;
  subheading?: string;
  style?: object;
  asterisk?: object;
  submitted?: boolean;
  checkWishlist?: () => boolean;
}

const PersonalInfoContainer = styled(Grid)`
  display: flex;
  margin-top: 50px !important;

  .MuiIconButton-root {
    margin-top: 0 !important;
    border: none !important;
    width: auto !important;
  }

  @media (max-width: ${smWidth}) {
    display: block;
  }
`;

const DateError = styled.p`
  font-size: 0.75rem;
  color: #f54394;
  margin: 3px 14px 0 14px;
`;

export default PersonalInfo;
